import { Hidden } from '@mui/material'
import cn from 'classnames'
import moment from 'moment'
import Image from 'next/image'
import { useRouter } from 'next/router'
import React, { memo, useEffect, useState } from 'react'
import icGraveyard from 'src/assets/ic-graveyard.png'
import { EditListingButton } from 'src/components/EditListingButton/EditListingButton'
import CompareButton from 'src/components/global/CompareButton'
import { FollowButton } from 'src/components/global/FollowButton'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import VendorLogo from 'src/components/global/VendorLogo/VendorLogo'
import { SolutionHeaderReviewStar } from 'src/components/SolutionHeaderReviewStar/SolutionHeaderReviewStar'
import { ProductPageTab } from 'src/components/Tabs/ProductPageTab'
import { SHOW_REVIEWS, THEME } from 'src/config/config'
import * as enVariables from 'src/config/config'
import { IEnum_Solution_Type, useSolutionVendorBySlugLazyQuery } from 'src/graphql/generated/hooks'
//import { IEnum_Solution_Item_Type, IEnum_Solution_Type } from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { DETAILS_TAB, REVIEWS_TAB, VENDOR_ANALYSIS_TAB } from 'src/types'
import {
  isConsolidationProductSolution,
  isGraveyardProductSolution,
  SolutionDataFragment,
} from 'src/utils/SolutionDataFragment'

import { SolutionTitle } from '../SolutionTitle/SolutionTitle'

interface SolutionHeaderProps {
  className?: string
  solution: SolutionDataFragment
  tabIdx: number
  showVendorAnalysis: boolean
  vendorUsers?: string[]
  onSelectTab: (tabIdx: number) => void
}

/**
 * Primary UI component for user interaction
 */
export const SolutionHeader: React.FC<SolutionHeaderProps> = memo(
  ({ className, solution, tabIdx, showVendorAnalysis, vendorUsers = [], onSelectTab }) => {
    const { session, status, isVendor } = useAuthenticated()
    const [loginUserId, setLoginUserId] = useState('')
    const [solutionVendorUsers, setSolutionVendorUsers] = useState<string[]>(vendorUsers)
    const displayName = solution.attributes?.displayName || ''
    const logo = useDefaultLogo(solution.attributes?.logo?.data?.attributes?.url)
    // const { title, byTitle } = getNameSegments(solution)
    const description = solution.attributes?.shortDescription || ''
    const router = useRouter()
    const { query } = router
    let vendorAllUsers = []
    const [loadsolutionData, { data: solutionData }] = useSolutionVendorBySlugLazyQuery()
    useEffect(() => {
      if (!solutionData) {
        loadsolutionData({
          variables: {
            slug: solution?.attributes?.slug || '',
            includeIlta: enVariables.THEME === 'ilta',
          },
          fetchPolicy: 'no-cache',
        })
      }
    }, [solutionData, loadsolutionData, solution?.attributes?.slug])

    useEffect(() => {
      if (solutionData && solutionData?.solutions?.data && solutionData?.solutions?.data?.length > 0) {
        vendorAllUsers = solutionData?.solutions?.data[0]?.attributes?.vendor?.data?.attributes?.users?.data.map(
          (user: any) => {
            return user?.attributes?.external_id || null
          },
        ) as string[]
        setSolutionVendorUsers(vendorAllUsers)
      }
    }, [solutionData])
    useEffect(() => {
      if (
        session &&
        status === 'authenticated' &&
        session?.user?.idToken != '' &&
        session?.user?.id != '' &&
        loginUserId == ''
      ) {
        setLoginUserId(session?.user?.id)
      }
    }, [session])

    const isGraveyard = isGraveyardProductSolution(solution)
    const isConsolidation = isConsolidationProductSolution(solution)
    let publishedDate = ''
    if (isGraveyard) {
      const date = solution.attributes?.graveyardData?.date
      publishedDate = date ? moment(date).format('MMM DD, YYYY') : ''
    }
    let acquiredBy = false
    if (isConsolidation) {
      let name: any = solution.attributes?.productData?.name

      const buyerData: any = solution?.attributes
      if (buyerData?.consolidationData?.buyer && buyerData?.consolidationData?.date) {
        name = buyerData?.consolidationData?.buyer
      }

      const date = solution.attributes?.consolidationData?.date
      publishedDate = date ? moment(date).format('MMM DD, YYYY') : ''
      publishedDate = `Acquired by ${name} on ${publishedDate}`
      if (solution.attributes?.consolidationData?.buyer && solution.attributes?.consolidationData?.date)
        acquiredBy = true
    }

    let iltaSponsorshipLogo = ''
    if ((solution.attributes?.iltaSponsorships?.data || []).length > 0) {
      iltaSponsorshipLogo = solution.attributes?.iltaSponsorships?.data[0].attributes?.logo?.data?.attributes?.url || ''
    }

    const solutionType = solution.attributes?.type || IEnum_Solution_Type.Product
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const solutionAttribute: any = solution?.attributes
    const productType = solutionAttribute ? solutionAttribute?.productType?.toLowerCase() : ''
    const ActionButtons = (
      <>
        {productType != 'graveyard' && (
          <FollowButton
            className="md:ml-[20px] mr-2"
            solutionType={solution.attributes?.type}
            id={solution.id!}
            displayName={displayName}
            isEnhanced={solution.attributes?.isEnhanced}
          />
        )}
        <div className="flex box-border h-[42px] w-[112px] border-solid border-[1px] border-mist rounded-[25px] bg-white items-center justify-center">
          <CompareButton className="items-center" slug={solution.attributes?.slug || ''} solutionType={solutionType} />
        </div>
      </>
    )
    const randomNum = Math.floor(Math.random() * 1000) // Generates a number between 0 and 999

    return (
      <div key={randomNum + 'SolutionHeader'} className={cn(className, 'flex flex-row justify-center bg-clouds')}>
        <div className="flex flex-row w-full justify-center">
          <div className="flex flex-col pl-[1.25rem] pr-[1.25rem] !max-w-[1100px] w-full pt-2 mt-[0.9375rem]">
            <div className="flex items-start">
              <div className="flex-1 flex flex-col md:flex-row items-center md:items-start sm:grid-cols-2">
                <VendorLogo className="md:mr-[1.6875rem]" alt={displayName} src={logo} />
                <div className="self-center w-full">
                  <div className="flex flex-row w-full">
                    <div className="flex flex-col w-full">
                      <div className="flex md:flex-row flex-col w-full justify-between">
                        <div className="flex flex-col flex-1 items-center md:items-start">
                          <SolutionTitle solution={solution} />
                          {SHOW_REVIEWS && (
                            <ThemeOnly theme="legaltech">
                              <SolutionHeaderReviewStar reviews={solution.attributes?.reviews?.data || []} />
                            </ThemeOnly>
                          )}
                        </div>
                        <ThemeOnly theme="legaltech">
                          {query['viewType'] !== 'manage' && <Hidden mdDown>{ActionButtons}</Hidden>}
                          {query['viewType'] === 'manage' &&
                            loginUserId &&
                            loginUserId != '' &&
                            solutionVendorUsers?.length > 0 &&
                            solutionVendorUsers?.includes(loginUserId) && (
                              <EditListingButton
                                slug={solution.attributes?.slug || ''}
                                solutionType={solution.attributes?.type.toString().toLowerCase() || ''}
                              />
                            )}
                        </ThemeOnly>
                      </div>
                    </div>
                  </div>
                  {!!publishedDate &&
                    (isGraveyard ? (
                      <div className="flex flex-row mb-1.5 h-[20px] w-fit bg-burlyWood rounded-[3px] items-center pl-[5px] pr-[5px]">
                        <Image src={icGraveyard} alt="" width={20} height={15} />
                        <p className="pl-[5px] h-[20px] text-textdark font-primary text-[15px] font-bold leading-[18px]">
                          {publishedDate}
                        </p>
                      </div>
                    ) : acquiredBy ? (
                      <p className="h-[20px] text-textdark font-primary text-[15px] font-bold leading-[18px]">
                        {publishedDate}
                      </p>
                    ) : (
                      ''
                    ))}
                  <Hidden mdDown>
                    <h2
                      className={cn(
                        'flex-1 text-[1.125rem] font-primary font-regular text-darkgray md:mt-[0px] mt-[1.25rem]',
                        {
                          '!font-tertiary !font-regular text-[16px]': THEME === 'ilta',
                        },
                      )}
                    >
                      <div className="value-proposition">
                        <HtmlContentViewer
                          html={description}
                          contentClassName={cn({
                            '!font-tertiary !font-regular text-[16px]': THEME === 'ilta',
                          })}
                        />
                      </div>
                    </h2>
                  </Hidden>
                </div>
              </div>
              {iltaSponsorshipLogo && (
                <Hidden mdDown>
                  <Image
                    src={iltaSponsorshipLogo}
                    width={200}
                    height={200}
                    className="w-[150px] h-auto ml-[50px]"
                    alt={''}
                  />
                </Hidden>
              )}
            </div>
            <Hidden mdUp>
              <ThemeOnly theme="legaltech">
                <div className="w-full flex flex-row mt-[20px] mb-[20px]">{ActionButtons}</div>
              </ThemeOnly>

              <h2
                className={cn(
                  'flex-1 text-[1.125rem] font-primary font-regular text-darkgray md:mt-[0px] mt-[1.25rem]',
                  {
                    '!font-tertiary !font-regular text-[16px]': THEME === 'ilta',
                  },
                )}
              >
                <HtmlContentViewer
                  html={description}
                  contentClassName={cn({
                    '!font-tertiary !font-regular text-[16px]': THEME === 'ilta',
                  })}
                />
              </h2>
            </Hidden>
            <div className="flex w-full mt-[3.75rem] h-[3.125rem]">
              <ProductPageTab id={DETAILS_TAB} activeTab={tabIdx} className="w-[117px]" onSelectTab={onSelectTab}>
                Details
              </ProductPageTab>
              {SHOW_REVIEWS && (
                <ThemeOnly theme="legaltech">
                  <ProductPageTab id={REVIEWS_TAB} activeTab={tabIdx} className="w-[117px]" onSelectTab={onSelectTab}>
                    Reviews
                  </ProductPageTab>
                </ThemeOnly>
              )}
              {!isVendor && session?.user.name && showVendorAnalysis && (
                <ProductPageTab
                  id={VENDOR_ANALYSIS_TAB}
                  activeTab={tabIdx}
                  className="w-[160px]"
                  onSelectTab={onSelectTab}
                >
                  Vendor Analysis
                </ProductPageTab>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  },
)
